/**
 * ConditionEvaluator
 * 
 * Evaluates conditions and triggers for flow gating and step branching.
 */

import { IConditionEvaluator } from '../types';
import {
  Condition,
  Trigger,
  TriggerContext,
  RouteMatch,
} from '../types';
import { useSquiggleStore } from '../../../stores/squiggleStore';

export class ConditionEvaluator implements IConditionEvaluator {
  private currentRoute: string = '/';

  /**
   * Set the current route for route-based conditions
   */
  setRoute(route: string): void {
    this.currentRoute = route;
  }

  /**
   * Evaluate a single condition
   */
  evaluate(condition: Condition): boolean {
    switch (condition.type) {
      case 'flag':
        return this.evaluateFlagCondition(condition.key, condition.equals);

      case 'userProp':
        return this.evaluateUserPropCondition(
          condition.key,
          condition.op,
          condition.value
        );

      case 'hasCompleted':
        return this.evaluateCompletionCondition(condition.key, condition.equals);

      case 'route':
        return this.evaluateRouteMatch(condition.match);

      default:
        console.warn('[ConditionEvaluator] Unknown condition type');
        return false;
    }
  }

  /**
   * Evaluate all conditions (AND logic)
   */
  evaluateAll(conditions: Condition[]): boolean {
    return conditions.every((condition) => this.evaluate(condition));
  }

  /**
   * Evaluate a trigger
   */
  evaluateTrigger(trigger: Trigger, context: TriggerContext): boolean {
    switch (trigger.type) {
      case 'route':
        return this.evaluateRouteTrigger(trigger, context);

      case 'event':
        return context.eventName === trigger.name;

      case 'manual':
        return context.isManual === true;

      case 'firstTime':
        return !this.hasCompletedFlow(trigger.key);

      default:
        return false;
    }
  }

  /**
   * Evaluate a flag condition
   */
  private evaluateFlagCondition(key: string, equals: boolean): boolean {
    const store = useSquiggleStore.getState();
    const value = store.milestones[key] ?? false;
    return value === equals;
  }

  /**
   * Evaluate a user property condition
   */
  private evaluateUserPropCondition(
    key: string,
    op: 'eq' | 'neq' | 'lt' | 'gt',
    value: unknown
  ): boolean {
    // Get user properties from store or context
    // For now, we'll use milestones as a simple key-value store
    const store = useSquiggleStore.getState();
    const userValue = store.milestones[key];

    switch (op) {
      case 'eq':
        return userValue === value;
      case 'neq':
        return userValue !== value;
      case 'lt':
        return typeof userValue === 'number' && typeof value === 'number'
          ? userValue < value
          : false;
      case 'gt':
        return typeof userValue === 'number' && typeof value === 'number'
          ? userValue > value
          : false;
      default:
        return false;
    }
  }

  /**
   * Evaluate a completion condition
   */
  private evaluateCompletionCondition(key: string, equals: boolean): boolean {
    const completed = this.hasCompletedFlow(key);
    return completed === equals;
  }

  /**
   * Check if a flow has been completed
   */
  private hasCompletedFlow(flowId: string): boolean {
    const store = useSquiggleStore.getState();
    return flowId in store.completedFlows;
  }

  /**
   * Evaluate a route match
   */
  private evaluateRouteMatch(match: RouteMatch): boolean {
    switch (match.kind) {
      case 'path':
        return this.currentRoute === match.path;

      case 'pattern':
        return this.matchPattern(match.pattern, this.currentRoute);

      case 'regex':
        try {
          const regex = new RegExp(match.regex);
          return regex.test(this.currentRoute);
        } catch {
          console.warn('[ConditionEvaluator] Invalid regex:', match.regex);
          return false;
        }

      default:
        return false;
    }
  }

  /**
   * Evaluate a route trigger
   */
  private evaluateRouteTrigger(
    trigger: Extract<Trigger, { type: 'route' }>,
    context: TriggerContext
  ): boolean {
    // Check if route matches
    if (!this.matchRoute(trigger.match, context.route)) {
      return false;
    }

    // Check when condition (default to 'enter')
    const when = trigger.when ?? 'enter';

    // For now, we treat all route matches as 'enter'
    // 'idle' would require tracking time on route
    return when === 'enter';
  }

  /**
   * Match a route against a RouteMatch
   */
  private matchRoute(match: RouteMatch, route: string): boolean {
    switch (match.kind) {
      case 'path':
        return route === match.path;

      case 'pattern':
        return this.matchPattern(match.pattern, route);

      case 'regex':
        try {
          const regex = new RegExp(match.regex);
          return regex.test(route);
        } catch {
          return false;
        }

      default:
        return false;
    }
  }

  /**
   * Match a URL pattern (simple wildcard matching)
   * Supports :param and * wildcards
   */
  private matchPattern(pattern: string, route: string): boolean {
    // Convert pattern to regex
    const regexPattern = pattern
      .replace(/:[^/]+/g, '[^/]+')  // :param -> match any segment
      .replace(/\*/g, '.*');         // * -> match anything

    try {
      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(route);
    } catch {
      return false;
    }
  }
}

export default ConditionEvaluator;
